import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme, getAllThemes, getThemeDisplayName } from '../../contexts/ThemeContext';
import Button from '../ui/Button';

interface HeaderProps {
  onLoginClick?: () => void;
  onRegisterClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLoginClick, onRegisterClick }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  const handleLogout = () => {
    logout();
  };

  const coffeeEmojis = ['☕', '🍺', '🌮'];
  const heartEmojis = ['🩵', '💗', '❤️'];
  const [hoverCount, setHoverCount] = useState(0);

  const handleMouseEnter = () => {
    setHoverCount(prev => prev + 1);
  };

const coffeeEmoji = coffeeEmojis[hoverCount % coffeeEmojis.length];
const heartEmoji = heartEmojis[hoverCount % heartEmojis.length];

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => window.location.href = '/'}
          >
            <img 
              src="/logo192.png" 
              alt="Fragrance Organizer Logo" 
              className="w-10 h-10 rounded-lg"
            />
            <div className="flex flex-col">
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight"
                style={{ filter: 'drop-shadow(2px 2px 2px rgba(0,0,0,0.3))' }}
              >
                Scentral
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight hidden sm:block">
              Fragrance Collection Organizer
              </span>
            </div>
          </motion.div>

          {/* Navigation & Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Theme Selector */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
                Theme:
              </label>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value as any)}
                className="appearance-none bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-300 dark:border-gray-600 w-16 sm:w-20"
                title="Theme"
              >
                {getAllThemes().map((themeOption) => (
                  <option key={themeOption} value={themeOption}>
                    {getThemeDisplayName(themeOption)}
                  </option>
                ))}
              </select>
            </div>

            {/* Authentication Actions */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-2 sm:space-x-4">
                <span className="text-sm text-gray-700 dark:text-gray-300 hidden md:block">
                  Welcome, {user?.username}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
                {/* Buy Me a Coffee */}
                <a
                  href="https://buymeacoffee.com/scentral"
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={handleMouseEnter}
                  className="relative inline-flex items-center px-3 py-1 rounded-full text-sm sm:text-base
                            border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400
                            overflow-hidden transition-all duration-300 ease-in-out group"
                >
                  {/* Background gradient overlay */}
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 
                                  -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-out" />

                  {/* Content */}
                  <span className="relative flex items-center z-10 transition-transform duration-300 group-hover:scale-105">
                    <span className="emoji text-lg transition-opacity duration-300 group-hover:opacity-0 text-center">
                      {coffeeEmoji}
                    </span>
                    <span className="emoji absolute text-lg transition-opacity duration-300 opacity-0 group-hover:opacity-100 text-center">
                      {heartEmoji}
                    </span>
                  </span>
                </a>
              </div>
            ) : (
              <div className="flex items-center space-x-1 sm:space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLoginClick}
                  className="px-2 sm:px-3"
                >
                  Login
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onRegisterClick}
                  className="px-2 sm:px-3 text-xs sm:text-sm"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
